import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';

interface FakeCursorProps {
  targetSelector: string;
  onComplete?: () => void;
  action?: 'click' | 'hover' | 'move';
  delay?: number;
  stepRunId?: string;
}

export function FakeCursor({ targetSelector, onComplete, action = 'click', delay = 300, stepRunId }: FakeCursorProps) {
  const [pos, setPos] = useState({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const [visible, setVisible] = useState(false);
  const [clicking, setClicking] = useState(false);
  const isMobile = window.innerWidth < 768;
  const runIdRef = useRef(stepRunId);

  useEffect(() => {
    runIdRef.current = stepRunId;
  }, [stepRunId]);

  useEffect(() => {
    if (isMobile) {
      onComplete?.();
      return;
    }

    const capturedRunId = stepRunId;
    const isStale = () => runIdRef.current !== capturedRunId;

    const startTimeout = setTimeout(() => {
      if (isStale()) return;
      setVisible(true);

      const findAndMove = () => {
        if (isStale()) return;
        const el = document.querySelector(targetSelector) as HTMLElement;
        if (!el) {
          onComplete?.();
          return;
        }

        const rect = el.getBoundingClientRect();
        const targetX = rect.left + rect.width / 2;
        const targetY = rect.top + rect.height / 2;

        setPos({ x: targetX, y: targetY });

        setTimeout(() => {
          if (isStale()) { setVisible(false); return; }
          if (action === 'click') {
            setClicking(true);
            setTimeout(() => {
              if (isStale()) { setVisible(false); return; }
              setClicking(false);
              el.classList.add('tour-hover');
              setTimeout(() => {
                if (isStale()) { setVisible(false); return; }
                el.click();
                el.classList.remove('tour-hover');
                setTimeout(() => {
                  setVisible(false);
                  if (!isStale()) onComplete?.();
                }, 200);
              }, 150);
            }, 200);
          } else if (action === 'hover') {
            el.classList.add('tour-hover');
            setTimeout(() => {
              if (isStale()) { setVisible(false); return; }
              el.classList.remove('tour-hover');
              setVisible(false);
              onComplete?.();
            }, 400);
          } else {
            setVisible(false);
            if (!isStale()) onComplete?.();
          }
        }, 600);
      };

      findAndMove();
    }, delay);

    return () => {
      clearTimeout(startTimeout);
      setVisible(false);
    };
  }, [targetSelector, action, delay, isMobile, stepRunId]);

  if (!visible || isMobile) return null;

  return createPortal(
    <div
      className="fixed z-[9998] pointer-events-none"
      style={{
        left: pos.x,
        top: pos.y,
        transition: 'left 0.5s cubic-bezier(0.4, 0, 0.2, 1), top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: 'translate(-4px, -2px)',
      }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>
        <path d="M5 3L19 12L12 13L9 20L5 3Z" fill="white" stroke="black" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
      {clicking && (
        <div
          className="absolute rounded-full bg-primary/30 animate-ping"
          style={{ width: 24, height: 24, top: -4, left: -4 }}
        />
      )}
    </div>,
    document.body
  );
}
